import React from 'react';
import {
  Alert as AlertData,
  AlertStatus,
  AlertStatusLabels
} from '@techgitter/contracts';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Box,
  Button,
  Typography
} from '@mui/material';
import { Note as NoteIcon, NavigateNext, NavigateBefore } from '@mui/icons-material';

interface AlertsTableProps {
  alerts: AlertData[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onRowClick: (alert: AlertData) => void;
  nextCursor?: string;
  onNextPage: () => void;
  onPrevPage: () => void;
  hasPreviousPage: boolean;
  loading?: boolean;
}

export const AlertsTable: React.FC<AlertsTableProps> = ({
  alerts,
  selectedIds,
  onSelectionChange,
  onRowClick,
  nextCursor,
  onNextPage,
  onPrevPage,
  hasPreviousPage,
  loading = false
}) => {
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectionChange(alerts.map(a => a.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedIds, id]);
    } else {
      onSelectionChange(selectedIds.filter(selectedId => selectedId !== id));
    }
  };

  const isSelected = (id: string) => selectedIds.includes(id);
  const isAllSelected = alerts.length > 0 && selectedIds.length === alerts.length;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < alerts.length;

  const getStatusChip = (status: AlertStatus) => {
    const label = AlertStatusLabels[status];
    let color: 'default' | 'primary' | 'success' | 'warning' = 'default';

    switch (status) {
      case AlertStatus.New:
        color = 'warning';
        break;
      case AlertStatus.Acknowledged:
        color = 'primary';
        break;
      case AlertStatus.Resolved:
        color = 'success';
        break;
      case AlertStatus.NoActionRequired:
        color = 'default';
        break;
    }

    return <Chip label={label} color={color} size="small" />;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ padding: 4, textAlign: 'center' }}>
        <Typography>Loading alerts...</Typography>
      </Box>
    );
  }

  if (alerts.length === 0) {
    return (
      <Box sx={{ padding: 4, textAlign: 'center' }}>
        <Typography color="textSecondary">No alerts found matching the current filters.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>Created On</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Message ID</TableCell>
              <TableCell>Changed By</TableCell>
              <TableCell>Changed On</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts.map((alert) => (
              <TableRow
                key={alert.id}
                hover
                onClick={() => onRowClick(alert)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected(alert.id)}
                    onChange={(e) => handleSelectOne(alert.id, e.target.checked)}
                  />
                </TableCell>
                <TableCell>{formatDate(alert.createdOn)}</TableCell>
                <TableCell>{getStatusChip(alert.status)}</TableCell>
                <TableCell>
                  <Chip
                    label={alert.isActive ? 'Active' : 'Inactive'}
                    color={alert.isActive ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {alert.summary || '-'}
                </TableCell>
                <TableCell>{alert.messageId || '-'}</TableCell>
                <TableCell>{alert.statusChangedBy || '-'}</TableCell>
                <TableCell>{formatDate(alert.statusChangedOn || undefined)}</TableCell>
                <TableCell>
                  {alert.notes && (
                    <NoteIcon fontSize="small" color="action" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, padding: 2 }}>
        <Button
          size="small"
          startIcon={<NavigateBefore />}
          onClick={onPrevPage}
          disabled={!hasPreviousPage}
        >
          Previous
        </Button>
        <Button
          size="small"
          endIcon={<NavigateNext />}
          onClick={onNextPage}
          disabled={!nextCursor}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
